import { useTranslation } from "react-i18next";
import { Q3DDataTable, Q3DDataTableHeaderType } from "../../Parts/Q3DDataTable";
import { useContext, useEffect, useState } from "react";
import {
  DETAIL_TAB2POINT_CLOUD_TYPE,
  EDialogType,
  EDisplayType,
  EViewerMode,
  ProjectDetailContext,
} from "../Interfaces/ProjectDetailInterfaces";
import HelmContext from "../Contexts/HelmContext";
import { ECoordinateTransformMode, EMPTY } from "../../config/componentsConfig";
import _ from "lodash";
import { Common } from "../../../../common/src";
import { Q3DHintPopover } from "../../Parts/Q3DHintPopOver";
import { POINT_CLOUD_TYPE } from "../../../../common/src/modules";

type Props = {};

export const HelmVerificationContent = (props: Props) => {
  const { t } = useTranslation();

  const {
    projectDetail,
    updateCurrentDialog,
    detailTab,
    viewer,
    currentViewerMode,
    currentPointCloud,
    displayType,
  } = useContext(ProjectDetailContext);
  const {
    currentHelmTab,
    coordinatePoints,
    verificationPoints,
    setVerificationPoints,
    fetchVerificationPoints,
    handleDelete,
    handleAdd,
    handleSave,
    handlePicking,
    handleAllPickPoints,
    verificationSaveFunction,
  } = useContext(HelmContext);

  useEffect(() => {
    const abortController = new AbortController();
    const abortControllerSignal = abortController.signal;
    fetchVerificationPoints(projectDetail, abortControllerSignal);

    return () => {
      abortController.abort();
    };
  }, []);

  useEffect(() => {
    if (
      currentHelmTab === ECoordinateTransformMode.VERIFICATION &&
      (currentPointCloud === POINT_CLOUD_TYPE.HELMERT ||
        (currentPointCloud === POINT_CLOUD_TYPE.SFM && projectDetail?.useViDoc))
    ) {
      handleAllPickPoints(ECoordinateTransformMode.VERIFICATION);
    }

    return () => {
      viewer.deleteAllPickPoint();
    };
  }, [currentHelmTab, currentPointCloud]);

  useEffect(() => {
    const operation =
      isVerificationPickable() &&
      currentViewerMode === EViewerMode.HELMERT_TRANSFORM
        ? "start"
        : "end";
    handlePicking(operation, ECoordinateTransformMode.VERIFICATION);
    if (currentViewerMode !== EViewerMode.HELMERT_TRANSFORM) {
      viewer.deleteAllPickPoint();
      viewer.endPicking();
    }
  }, [detailTab, currentHelmTab, verificationPoints, currentViewerMode]);

  const isVerificationPickable = () => {
    return (
      verificationPoints[verificationPoints.length - 1]?.name === EMPTY &&
      ((DETAIL_TAB2POINT_CLOUD_TYPE(detailTab) ===
        Common.POINT_CLOUD_TYPE.SFM &&
        projectDetail.useViDoc) ||
        DETAIL_TAB2POINT_CLOUD_TYPE(detailTab) ===
          Common.POINT_CLOUD_TYPE.HELMERT) &&
      currentHelmTab === ECoordinateTransformMode.VERIFICATION
    );
  };

  const headers = [
    {
      key: "tool",
      name: "",
      type: "icon",
    },
    {
      key: "correspondingPointId",
      name: t("VALIDATION_POINT_NAME"),
      type: "select",
    },
    {
      key: "timeStamp",
      name: t("DATE"),
      type: "date",
    },
    {
      key: "trueX",
      name: t("VALID_POINT_X_COORD"),
      type: "number",
      sx: {
        textAlign: "right",
        paddingRight: displayType === EDisplayType.iOS ? "0" : "19px",
      },
    },
    {
      key: "trueY",
      name: t("VALID_POINT_Y_COORD"),
      type: "number",
      sx: {
        textAlign: "right",
        paddingRight: displayType === EDisplayType.iOS ? "0" : "19px",
      },
    },
    {
      key: "trueZ",
      name: t("VALID_POINT_Z_COORD"),
      type: "number",
      sx: {
        textAlign: "right",
        paddingRight: displayType === EDisplayType.iOS ? "0" : "19px",
      },
    },
    {
      key: "x",
      name: t("ORIGINAL_X"),
      type: "number",
      sx: {
        textAlign: "right",
        paddingRight: displayType === EDisplayType.iOS ? "0" : "19px",
      },
    },
    {
      key: "y",
      name: t("ORIGINAL_Y"),
      type: "number",
      sx: {
        textAlign: "right",
        paddingRight: displayType === EDisplayType.iOS ? "0" : "19px",
      },
    },
    {
      key: "z",
      name: t("ORIGINAL_Z"),
      type: "number",
      sx: {
        textAlign: "right",
        paddingRight: displayType === EDisplayType.iOS ? "0" : "19px",
      },
    },
    {
      key: "dx",
      name: t("X_ERROR"),
      type: "number",
      rightIcon: (
        <Q3DHintPopover
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          content={
            t("X_ERROR") +
            " = " +
            t("VALID_POINT_X_COORD") +
            " - " +
            t("ORIGINAL_X")
          }
        />
      ),
      sx: {
        textAlign: "right",
        paddingRight: displayType === EDisplayType.iOS ? "0" : "15px",
      },
    },
    {
      key: "dy",
      name: t("Y_ERROR"),
      type: "number",
      rightIcon: (
        <Q3DHintPopover
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          content={
            t("Y_ERROR") +
            " = " +
            t("VALID_POINT_Y_COORD") +
            " - " +
            t("ORIGINAL_Y")
          }
        />
      ),
      sx: {
        textAlign: "right",
        paddingRight: displayType === EDisplayType.iOS ? "0" : "15px",
      },
    },
    {
      key: "dz",
      name: t("Z_ERROR"),
      type: "number",
      rightIcon: (
        <Q3DHintPopover
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          content={
            t("Z_ERROR") +
            " = " +
            t("VALID_POINT_Z_COORD") +
            " - " +
            t("ORIGINAL_Z")
          }
        />
      ),
      sx: {
        textAlign: "right",
        paddingRight: displayType === EDisplayType.iOS ? "0" : "15px",
      },
    },
  ] as Q3DDataTableHeaderType[];

  const updateRowsData = (id: string, key: string, value: string) => {
    setVerificationPoints(
      verificationPoints.map((data) => {
        if (data.id === id) {
          (data as any)[key] = value;
          if (key === "correspondingPointId") {
            const correspondingPoint = coordinatePoints?.find(
              (correspondPoint) => correspondPoint.id === value
            );
            data.trueX = correspondingPoint?.x ? correspondingPoint.x : 0;
            data.trueY = correspondingPoint?.y ? correspondingPoint.y : 0;
            data.trueZ = correspondingPoint?.z ? correspondingPoint.z : 0;
            data.timeStamp = correspondingPoint?.timeStamp
              ? correspondingPoint.timeStamp
              : new Date(0);
            data.dx = data.trueX - data.x;
            data.dy = data.trueY - data.y;
            data.dz = data.trueZ - data.z;
          }
        }
        return data;
      })
    );
    handleAllPickPoints(ECoordinateTransformMode.VERIFICATION);
  };

  // 保存制御処理
  const debouncedSave = _.debounce(verificationSaveFunction, 1000);

  return (
    <Q3DDataTable
      headers={headers}
      rows={verificationPoints}
      selectItems={coordinatePoints.map((item) => {
        return { label: item.pointName, value: item.id };
      })}
      onClickHeaderButton={() => {
        updateCurrentDialog(EDialogType.DELETE_ALL_POINTS);
      }}
      onClickRowButton={(row: string, isSave: boolean) => {
        if (isSave) {
          handleSave(ECoordinateTransformMode.VERIFICATION, row);
        } else {
          handleDelete(ECoordinateTransformMode.VERIFICATION, row);
        }
      }}
      onClickBottomButton={() => {
        handleAdd(ECoordinateTransformMode.VERIFICATION);
      }}
      onChange={(id: string, key: string, value: any) => {
        updateRowsData(id, key, value);
        if (id !== "new") {
          debouncedSave(id, key, value);
        }
      }}
    />
  );
};
